import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';

const TalentLists = () => {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newList, setNewList] = useState({ name: '', description: '', deal_id: '' });
  const [deals, setDeals] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ count: 8, tiers: [], categories: '', aud_18_34_min: '', aud_male_min: '', bss_min: '', budget: '', exclude_conflicts_with: '' });
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', t1: 0, t2: 0, t3: 0 });

  useEffect(() => { fetchLists(); fetchDeals(); fetchTemplates(); }, []);

  const fetchLists = async () => {
    try { const res = await api.get('/talent-lists'); setLists(res.data.lists || []); }
    catch (e) {} finally { setLoading(false); }
  };

  const fetchDeals = async () => {
    try { const res = await api.get('/deals'); setDeals(res.data.deals || []); } catch (e) {}
  };

  const fetchTemplates = async () => {
    try { const res = await api.get('/talent-list-templates'); setTemplates(res.data.templates || []); } catch (e) {}
  };

  const createTemplate = async () => {
    const { name, description, t1, t2, t3 } = templateForm;
    if (!name) return;
    const tier_slots = [];
    for (let i = 0; i < (parseInt(t1)||0); i++) tier_slots.push({ tier: 1 });
    for (let i = 0; i < (parseInt(t2)||0); i++) tier_slots.push({ tier: 2 });
    for (let i = 0; i < (parseInt(t3)||0); i++) tier_slots.push({ tier: 3 });
    try {
      await api.post('/talent-list-templates', { name, description, tier_slots });
      setShowTemplateForm(false);
      setTemplateForm({ name: '', description: '', t1: 0, t2: 0, t3: 0 });
      fetchTemplates();
    } catch (e) { alert('Failed to create template'); }
  };

  const deleteTemplate = async (tid) => {
    try { await api.delete(`/talent-list-templates/${tid}`); fetchTemplates(); } catch (e) {}
  };

  const useTemplate = (tmpl) => {
    const slots = typeof tmpl.tier_slots === 'string' ? JSON.parse(tmpl.tier_slots) : tmpl.tier_slots;
    const tiers = slots.map(s => s.tier);
    setSuggestForm(prev => ({
      ...prev,
      count: slots.length,
      tiers: [...new Set(tiers)]
    }));
    setShowSuggest(true);
  };

  const handleCreate = async () => {
    if (!newList.name) return;
    try {
      const res = await api.post('/talent-lists', newList);
      navigate(`/talent-lists/${res.data.list.id}`);
    } catch (e) { alert('Failed to create list'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this list?')) return;
    try { await api.delete(`/talent-lists/${id}`); fetchLists(); } catch (e) { alert('Failed'); }
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      const cats = suggestForm.categories ? suggestForm.categories.split(',').map(c => c.trim()).filter(Boolean) : [];
      const res = await api.post('/talent-lists/smart-suggest', {
        count: suggestForm.count, tiers: suggestForm.tiers, categories: cats,
        aud_18_34_min: suggestForm.aud_18_34_min, aud_male_min: suggestForm.aud_male_min,
        bss_min: suggestForm.bss_min, exclude_conflicts_with: suggestForm.exclude_conflicts_with
      });
      setSuggestions(res.data);
    } catch (e) { alert('Suggest failed'); } finally { setSuggesting(false); }
  };

  const createListFromSuggestions = async () => {
    if (!suggestions) return;
    const name = prompt('List name?', 'Smart Suggest ' + new Date().toLocaleDateString());
    if (!name) return;
    try {
      const res = await api.post('/talent-lists', { name });
      await api.post(`/talent-lists/${res.data.list.id}/items`, { talent_ids: suggestions.suggested.map(t => t.id) });
      navigate(`/talent-lists/${res.data.list.id}`);
    } catch (e) { alert('Failed'); }
  };

  const formatNum = (n) => { if (!n) return '0'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(0)+'K'; return String(n); };
  const tierCls = (t) => t === 1 ? 'bg-amber-500/20 text-amber-400' : t === 2 ? 'bg-zinc-400/20 text-zinc-300' : t === 3 ? 'bg-orange-800/20 text-orange-400' : 'bg-zinc-800 text-zinc-600';

  return (
    <div className="p-8 min-w-0">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-4 mb-1">
            <Link to="/talent-metrics" className="text-zinc-400 hover:text-white text-sm">Metrics</Link>
            <Link to="/talent" className="text-zinc-400 hover:text-white text-sm">Network</Link>
            <span className="text-white text-sm font-semibold border-b-2 border-nextide-turquoise pb-0.5">Lists</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Talent Lists</h1>
          <p className="text-zinc-400 text-sm mt-1">{lists.length} list{lists.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowSuggest(true)} className="bg-zinc-800 border border-zinc-700 text-white px-4 py-2 rounded-lg text-sm hover:border-zinc-600">
            Smart Suggest
          </button>
          <button onClick={() => setShowCreate(true)} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold">
            + New List
          </button>
        </div>
      </div>

      {loading ? <div className="text-zinc-500 py-16 text-center">Loading...</div> : lists.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-zinc-500 text-lg mb-2">No talent lists yet</div>
          <div className="text-zinc-600 text-sm">Create a list and add creators from the Network tab</div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {lists.map(l => (
            <div key={l.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-zinc-600 transition-colors cursor-pointer"
              onClick={() => navigate(`/talent-lists/${l.id}`)}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="text-white font-semibold">{l.name}</div>
                  {l.deal_name && <div className="text-zinc-500 text-xs mt-0.5">Linked: {l.deal_name}</div>}
                </div>
                <button onClick={e => { e.stopPropagation(); handleDelete(l.id); }} className="text-zinc-600 hover:text-red-400 text-xs">Delete</button>
              </div>
              {l.description && <div className="text-zinc-400 text-sm mb-3 line-clamp-2">{l.description}</div>}
              <div className="flex items-center gap-3 text-xs text-zinc-500">
                <span>{l.item_count || 0} creators</span>
                <span>{new Date(l.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Templates */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">List Templates</h2>
          <button onClick={() => setShowTemplateForm(!showTemplateForm)} className="text-nextide-turquoise text-sm hover:underline">
            {showTemplateForm ? 'Cancel' : '+ New Template'}
          </button>
        </div>

        {showTemplateForm && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-4">
            <div className="grid grid-cols-4 gap-3 mb-3">
              <div className="col-span-2"><label className="block text-xs text-zinc-400 mb-1">Template Name</label>
                <input type="text" value={templateForm.name} onChange={e => setTemplateForm({...templateForm, name: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder='Standard 8-Creator' /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">T1 Slots</label>
                <input type="number" min="0" value={templateForm.t1} onChange={e => setTemplateForm({...templateForm, t1: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">T2 Slots</label>
                <input type="number" min="0" value={templateForm.t2} onChange={e => setTemplateForm({...templateForm, t2: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
            </div>
            <div className="grid grid-cols-4 gap-3 mb-3">
              <div className="col-span-2"><label className="block text-xs text-zinc-400 mb-1">Description</label>
                <input type="text" value={templateForm.description} onChange={e => setTemplateForm({...templateForm, description: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">T3 Slots</label>
                <input type="number" min="0" value={templateForm.t3} onChange={e => setTemplateForm({...templateForm, t3: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div className="flex items-end">
                <button onClick={createTemplate} disabled={!templateForm.name}
                  className="w-full bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">Save</button>
              </div>
            </div>
          </div>
        )}

        {templates.length > 0 ? (
          <div className="grid grid-cols-4 gap-3">
            {templates.map(tmpl => {
              const slots = typeof tmpl.tier_slots === 'string' ? JSON.parse(tmpl.tier_slots) : (tmpl.tier_slots || []);
              const t1 = slots.filter(s => s.tier === 1).length;
              const t2 = slots.filter(s => s.tier === 2).length;
              const t3 = slots.filter(s => s.tier === 3).length;
              return (
                <div key={tmpl.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-600 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-white text-sm font-semibold">{tmpl.name}</div>
                    <button onClick={() => deleteTemplate(tmpl.id)} className="text-zinc-600 hover:text-red-400 text-xs">x</button>
                  </div>
                  <div className="flex gap-2 mb-2 text-xs">
                    {t1 > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-semibold">{t1}x T1</span>}
                    {t2 > 0 && <span className="px-1.5 py-0.5 rounded bg-zinc-400/20 text-zinc-300 font-semibold">{t2}x T2</span>}
                    {t3 > 0 && <span className="px-1.5 py-0.5 rounded bg-orange-800/20 text-orange-400 font-semibold">{t3}x T3</span>}
                    <span className="text-zinc-500">{slots.length} total</span>
                  </div>
                  {tmpl.description && <div className="text-zinc-500 text-xs mb-2">{tmpl.description}</div>}
                  <button onClick={() => useTemplate(tmpl)} className="text-nextide-turquoise text-xs hover:underline">Use Template</button>
                </div>
              );
            })}
          </div>
        ) : !showTemplateForm && (
          <div className="text-zinc-600 text-sm">No templates yet. Create one to standardize your list builds.</div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-white mb-4">New Talent List</h2>
            <div className="space-y-3">
              <div><label className="block text-xs text-zinc-400 mb-1">Name *</label>
                <input type="text" value={newList.name} onChange={e => setNewList({...newList, name: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Nike Q2 Shortlist" autoFocus /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Link to Deal (optional)</label>
                <select value={newList.deal_id} onChange={e => setNewList({...newList, deal_id: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="">None</option>
                  {deals.map(d => <option key={d.id} value={d.id}>{d.name} — {d.account}</option>)}
                </select></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Description</label>
                <textarea value={newList.description} onChange={e => setNewList({...newList, description: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" rows={2} /></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowCreate(false)} className="flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={handleCreate} disabled={!newList.name} className="flex-1 bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Suggest Modal */}
      {showSuggest && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => { setShowSuggest(false); setSuggestions(null); }}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-800 flex justify-between items-center sticky top-0 bg-zinc-900 z-10">
              <div><h2 className="text-lg font-bold text-white">Smart Suggest</h2>
                <p className="text-zinc-400 text-sm">Describe your campaign needs and get talent recommendations</p></div>
              <button onClick={() => { setShowSuggest(false); setSuggestions(null); }} className="text-zinc-400 hover:text-white text-xl">x</button>
            </div>
            <div className="p-6">
              {!suggestions ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-xs text-zinc-400 mb-1">Number of creators</label>
                      <input type="number" value={suggestForm.count} onChange={e => setSuggestForm({...suggestForm, count: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
                    <div><label className="block text-xs text-zinc-400 mb-1">Tiers</label>
                      <div className="flex gap-2 mt-1">
                        {[1,2,3].map(t => (
                          <button key={t} onClick={() => setSuggestForm(prev => ({...prev, tiers: prev.tiers.includes(t) ? prev.tiers.filter(x=>x!==t) : [...prev.tiers, t]}))}
                            className={`px-3 py-1 rounded text-sm ${suggestForm.tiers.includes(t) ? 'bg-nextide-turquoise text-black font-semibold' : 'bg-zinc-800 text-zinc-400'}`}>
                            T{t}
                          </button>
                        ))}
                      </div></div>
                  </div>
                  <div><label className="block text-xs text-zinc-400 mb-1">Categories (comma separated)</label>
                    <input type="text" value={suggestForm.categories} onChange={e => setSuggestForm({...suggestForm, categories: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Shooters, Variety, Minecraft" /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><label className="block text-xs text-zinc-400 mb-1">Min Aud 18-34 %</label>
                      <input type="number" value={suggestForm.aud_18_34_min} onChange={e => setSuggestForm({...suggestForm, aud_18_34_min: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="60" /></div>
                    <div><label className="block text-xs text-zinc-400 mb-1">Min Aud Male %</label>
                      <input type="number" value={suggestForm.aud_male_min} onChange={e => setSuggestForm({...suggestForm, aud_male_min: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="70" /></div>
                    <div><label className="block text-xs text-zinc-400 mb-1">Min BSS</label>
                      <input type="number" step="0.1" value={suggestForm.bss_min} onChange={e => setSuggestForm({...suggestForm, bss_min: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="3.0" /></div>
                  </div>
                  <div><label className="block text-xs text-zinc-400 mb-1">Exclude conflicts with (brand name)</label>
                    <input type="text" value={suggestForm.exclude_conflicts_with} onChange={e => setSuggestForm({...suggestForm, exclude_conflicts_with: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Red Bull" /></div>
                  <button onClick={handleSuggest} disabled={suggesting}
                    className="w-full bg-nextide-turquoise text-black px-4 py-3 rounded-lg font-semibold disabled:opacity-50">
                    {suggesting ? 'Finding talent...' : 'Find Talent'}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-white font-semibold">{suggestions.suggested.length} recommended creators</div>
                    <button onClick={() => setSuggestions(null)} className="text-zinc-400 text-sm hover:underline">Adjust criteria</button>
                  </div>
                  <div className="space-y-2 mb-4">
                    {suggestions.suggested.map(t => (
                      <div key={t.id} className="flex items-center justify-between bg-zinc-800 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tierCls(t.talent_tier)}`}>T{t.talent_tier || '-'}</span>
                          <div>
                            <div className="text-white text-sm font-medium">{t.name}</div>
                            <div className="text-zinc-500 text-xs">{t.primary_category || ''}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-nextide-turquoise">{formatNum(t.total_following)}</span>
                          <span className="text-purple-400">CCV {formatNum(t.twitch_avg_ccv)}</span>
                          <span className="text-zinc-400">Fit: {t.fit_score}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={createListFromSuggestions}
                    className="w-full bg-nextide-turquoise text-black px-4 py-3 rounded-lg font-semibold">
                    Create List with These {suggestions.suggested.length} Creators
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentLists;
